<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { useSmarticoMiniGames } from '@/stores/smartico/miniGames';
import { useSmarticoApi } from '@/composables/useSmarticoApi';
import { useSmarticoInit } from '@/stores/smartico/init';
import { useAuthStore } from '@/stores/auth';
import { useAuth } from '@/composables/useAuth';

// Esta rota (que já existe em prod) tem dois modos — rotas NOVAS são bloqueadas
// no deploy, então a roleta antiga reusa este mesmo path via query:
//
// - padrão (deep-link/menu): abre a roletinha NOVA (modal global) e volta pra
//   home. O id do link vira o fallback pra roleta antiga.
// - ?legacy=1: renderiza a roleta ANTIGA (iframe do Smartico inline). É pra onde
//   o fallback navega quando o core não tem roleta pra esse user
//   (ver stores/roulette.ts → openLegacyRoulette).
const route = useRoute();
const router = useRouter();
const { standardTitleSuffix } = getStandardSeoSuffixes();

const isLegacy = computed(() => route.query.legacy !== undefined);

const miniGameId = computed(() =>
  parseInt(String((route.params as { id: string }).id)),
);

const smarticoMiniGamesStore = useSmarticoMiniGames();
const { loadMiniGames } = smarticoMiniGamesStore;
const { miniGames } = storeToRefs(smarticoMiniGamesStore);
const { smarticoInstance } = useSmarticoApi();
const { isSuccessCheckUserIdentify } = storeToRefs(useSmarticoInit());

const authStore = useAuthStore();
const { isAuthenticated } = storeToRefs(authStore);
const { openLogin } = useAuth();

const isLoading = ref(true);
const smarticoIframeRef = ref<HTMLIFrameElement | null>(null);

// Termos do minigame renderizados como accordion (cada seção abre/fecha
// independente). Título e disclaimer ficam fixos fora do accordion.
const termsSections = [
  {
    title: '1. Disposições Gerais',
    items: [
      '1.1. Estes Termos regulam o Minigame de Roleta Diária da Aposta Tudo — atividade promocional e de entretenimento interna, baseada em sorte e geração aleatória de resultados.',
      '1.2. O minigame não constitui promessa de lucro, investimento ou garantia de ganho. A participação implica a aceitação integral destes Termos.',
    ],
  },
  {
    title: '2. Disponibilidade de Uso',
    items: [
      '2.1. O minigame pode ser utilizado uma vez a cada 24 horas.',
      '2.2. O período de 24 horas é contado a partir do último giro realizado, de modo que cada jogador tenha a mesma oportunidade de participação.',
    ],
  },
  {
    title: '3. Requisito de Participação',
    items: [
      '3.1. Podem participar os jogadores maiores de 18 anos completos ou mais; possuam conta registrada e verificada na plataforma conforme os procedimentos de verificação exigidos; tenham aceito integralmente receber bônus e promoções.',
      '3.2. Não há exigência de nível mínimo.',
    ],
  },
  {
    title: '4. Funcionamento e Premiações',
    items: [
      '4.1. A cada giro, o jogador recebe uma premiação aleatória, determinada por sistema automatizado e imparcial, sem intervenção manual.',
      '4.2. As premiações podem incluir giros gratuitos e outros benefícios promocionais definidos em cada edição do minigame.',
      '4.3. Transparência: os prêmios possíveis e a probabilidade de ocorrência de cada um são exibidos no próprio minigame, pelo ícone "?", antes do giro.',
      '4.4. Os prêmios promocionais (como giros gratuitos) não são sacáveis em dinheiro e seguem as regras específicas de cada tipo de recompensa (validade, jogos elegíveis e, quando houver, rollover), informadas no momento da concessão.',
    ],
  },
  {
    title: '5. Restrições e Conduta',
    items: [
      '5.1. Não é permitido burlar, manipular ou utilizar métodos automatizados para obter giros extras ou resultados diferentes dos previstos.',
      '5.2. Diante de indícios concretos de uso indevido, a Aposta Tudo poderá cancelar a premiação concedida indevidamente e suspender a conta para análise. Antes de decisão definitiva que implique perda de prêmio ou bloqueio de conta, o jogador será notificado e poderá apresentar esclarecimentos em prazo razoável, salvo obrigação legal em sentido diverso.',
    ],
  },
  {
    title: '6. Alterações e Ajustes',
    items: [
      '6.1. A Aposta Tudo poderá alterar o funcionamento do minigame — valores das premiações, probabilidades de cada prêmio e requisitos de participação — mediante aviso prévio, com as informações atualizadas exibidas no ícone "?".',
      '6.2. As alterações não afetam giros já disponíveis ou concedidos ao jogador antes da mudança.',
    ],
  },
  {
    title: '7. Transparência e Segurança',
    items: [
      '7.1. A distribuição de prêmios é totalmente automatizada, sem intervenção manual, assegurando aleatoriedade e imparcialidade.',
      '7.2. As interações com o minigame são registradas de forma auditável.',
    ],
  },
  {
    title: '8. Jogo Responsável e Proteção de Dados',
    items: [
      '8.1. Atividade destinada a maiores de 18 anos. Apostar pode causar dependência e levar à perda de dinheiro. Aposta não é investimento. Jogue com responsabilidade. O jogador pode definir limites e solicitar autoexclusão nos canais oficiais.',
      '8.2. Os dados são tratados conforme a Lei nº 13.709/2018 (LGPD) e a Política de Privacidade da plataforma.',
    ],
  },
];

const openTerms = ref<number[]>([]);

const isTermOpen = (index: number) => openTerms.value.includes(index);

const toggleTerm = (index: number) => {
  const position = openTerms.value.indexOf(index);

  if (position === -1) {
    openTerms.value.push(index);
  } else {
    openTerms.value.splice(position, 1);
  }
};

const currentMiniGame = computed(() =>
  miniGames.value.find((game) => game.id === miniGameId.value),
);

useSeo({
  title: isLegacy.value
    ? `${currentMiniGame.value?.name || 'Roletinha'} ${standardTitleSuffix}`
    : `Roletinha ${standardTitleSuffix}`,
});

// Modo padrão: abre o modal novo e volta pra home. O id do link é o fallback
// pra roleta antiga caso o core não tenha roleta pra esse user.
onMounted(() => {
  if (isLegacy.value) return;

  useRouletteStore().open({ fallbackMiniGameId: miniGameId.value });
  navigateTo(localeLink('/'), { replace: true });
});

// Fallback: o usuário chegou aqui pela roletinha (home/menu), não pela lista de
// mini-games — então "voltar" leva pra home.
const handleBackNavigation = () => {
  router.push(localeLink('/'));
};

const initializeMiniGame = async () => {
  if (!authStore.isAuthenticated) {
    openLogin();

    return;
  }

  if (isNaN(miniGameId.value)) {
    return;
  }

  if (miniGames.value.length === 0) {
    await loadMiniGames();
  }

  await new Promise<void>((resolve) => {
    const checkSmarticoInstanceReady = setInterval(() => {
      if (
        smarticoInstance.value &&
        typeof smarticoInstance.value.miniGame === 'function' &&
        isSuccessCheckUserIdentify.value
      ) {
        clearInterval(checkSmarticoInstanceReady);
        resolve();
      }
    }, 50);
  });

  await new Promise<void>((resolve) => {
    const checkIframeReady = setInterval(() => {
      if (smarticoIframeRef.value) {
        clearInterval(checkIframeReady);
        resolve();
      }
    }, 50);
  });

  const playOptions: {
    iframe?: string;
    inline?: boolean;
    height?: string;
    width?: string;
    zoom?: number;
    theme?: string;
  } = {
    iframe: 'smartico-minigame-frame',
    inline: true,
  };

  // As dimensões do iframe ficam no CSS (.smartico-minigame-iframe) com
  // !important. O Smartico reescreve os estilos inline do iframe depois que
  // termina de carregar — sem o !important ele sobrescrevia o scale/height e o
  // iframe "encolhia". O !important garante que o nosso layout sempre vença.
  if (window._smartico && typeof window._smartico.miniGame === 'function') {
    window._smartico.miniGame(miniGameId.value, playOptions);
  }

  if (
    smarticoInstance.value &&
    typeof smarticoInstance.value.on === 'function'
  ) {
    smarticoInstance.value.on('gf_closing', () => {
      router.push(localeLink('/'));
    });
  }
};

// Só o modo legacy inicializa o iframe do Smartico. No modo padrão a rota já
// redirecionou pra home no onMounted, então esta lógica fica inerte.
watch(
  [isAuthenticated, smarticoInstance],
  async ([authenticated, smarticoApi]) => {
    if (!isLegacy.value) return;

    if (!authenticated) {
      openLogin();
      router.push(localeLink('/'));

      return;
    }

    if (authenticated && smarticoApi) {
      isLoading.value = false;

      await initializeMiniGame();
    }
  },
  { immediate: true },
);
</script>

<template>
  <div
    v-if="isLegacy"
    class="mini-game-page"
  >
    <BaseScreenHeader
      heading-tag="h1"
      :on-back="handleBackNavigation"
      :title="currentMiniGame?.name || 'Roletinha'"
    />

    <div class="mini-game-main-content-wrapper">
      <div
        v-if="isLoading"
        class="loading-indicator text-texts text-[14px]"
      >
        Carregando roletinha...
      </div>
      <div
        v-else
        class="games mini-game-content-wrapper"
      >
        <section class="spacing">
          <div class="games_content">
            <div class="mini-game-iframe-container">
              <iframe
                id="smartico-minigame-frame"
                ref="smarticoIframeRef"
                class="smartico-minigame-iframe"
                title="Smartico Mini-game"
              ></iframe>
            </div>
          </div>
        </section>

        <section class="terms-section">
          <p class="terms-title">
            Termos e Condições — Minigame de Roleta Diária
          </p>

          <ul class="terms-accordion">
            <li
              v-for="(section, index) in termsSections"
              :key="section.title"
              class="terms-item"
              :class="{ 'is-open': isTermOpen(index) }"
            >
              <button
                :aria-expanded="isTermOpen(index)"
                class="terms-item__header"
                type="button"
                @click="toggleTerm(index)"
              >
                <span class="terms-item__title">{{ section.title }}</span>
                <NuxtIcon
                  class="terms-item__icon"
                  name="arrows/angle_down"
                />
              </button>

              <div
                v-show="isTermOpen(index)"
                class="terms-item__body"
              >
                <p
                  v-for="(text, textIndex) in section.items"
                  :key="textIndex"
                  class="terms-text"
                >
                  {{ text }}
                </p>
              </div>
            </li>
          </ul>

          <p class="terms-disclaimer">
            🛡️ Minigame baseado em sorte, com probabilidades informadas no ícone
            "?". Os prêmios promocionais (como giros grátis) não são sacáveis em
            dinheiro. Não há promessa de ganho. +18. Jogue com
            responsabilidade.<br />
            Ministério da Fazenda adverte: Aposta não é investimento.
          </p>
        </section>
      </div>
    </div>
  </div>
  <div v-else />
</template>

<style scoped lang="scss">
.mini-game-page {
  @apply flex flex-col;
}

.mini-game-main-content-wrapper {
  @apply w-full max-w-full;
}

.loading-indicator {
  @apply text-xl text-center my-10;
}

.games {
  @apply flex flex-col xl:pt-5;

  .spacing {
    @apply p-3 pt-0 px-0;
  }

  .games_content {
    @apply flex justify-center bg-bg-primary;

    .mini-game-iframe-container {
      width: 100%;
      height: auto;
      border: 1px solid #cccccc10;
      border-radius: 8px;
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }

    .smartico-minigame-iframe {
      flex-grow: 1;
      /* !important vence os estilos inline que o Smartico reaplica após carregar */
      display: block !important;
      width: 153.85% !important;
      height: 100vh !important;
      transform: scale(0.65) !important;
      transform-origin: top left !important;
      margin-bottom: -35vh !important;
    }
  }
}

.terms-section {
  @apply mt-4 mb-8 w-full;
  @apply rounded-2xl border border-white/5 bg-bg-secondary/40 p-4 sm:p-6;

  .terms-title {
    @apply mb-4 text-center text-base font-bold text-texts sm:text-lg;
    @apply relative pb-3;

    &::after {
      content: '';
      @apply absolute bottom-0 left-1/2 h-[3px] w-16 -translate-x-1/2 rounded-full;
      background: var(--ds-primary, theme('colors.primary'));
    }
  }

  .terms-accordion {
    @apply mt-5 grid grid-cols-1 items-start gap-3;
    @apply sm:grid-cols-2 lg:grid-cols-3;
  }

  .terms-item {
    @apply overflow-hidden rounded-xl border border-white/5 bg-white/[0.02];
    @apply transition-colors;

    &.is-open {
      @apply border-white/10 bg-white/[0.04];

      .terms-item__icon {
        @apply rotate-180;
      }
    }
  }

  .terms-item__header {
    @apply flex w-full items-center justify-between gap-3 px-4 py-3.5 text-left;
    @apply cursor-pointer select-none bg-transparent transition-colors;

    &:hover {
      @apply bg-white/[0.03];
    }
  }

  .terms-item__title {
    @apply text-sm font-semibold text-texts sm:text-[15px];
  }

  .terms-item__icon {
    @apply shrink-0 text-base text-texts/60 transition-transform duration-200;
  }

  .terms-item__body {
    @apply px-4 pb-4 pt-0.5;
  }

  .terms-text {
    @apply mb-2 text-[13px] leading-relaxed text-texts/70;

    &:last-child {
      @apply mb-0;
    }
  }

  .terms-disclaimer {
    @apply mt-6 rounded-xl border border-white/5 bg-black/20 p-4;
    @apply text-center text-[12px] leading-relaxed text-texts/60;
  }
}
</style>
